// Three different error shapes hit this depending on which layer rejected the
// request: (1) app-owned routes return `{ error: "a plain string" }`; (2) a
// zValidator-rejected request returns Hono's default shape instead —
// `{ error: { name: 'ZodError', message: '<JSON string of issue objects>' } }`
// — treating `err.error` as always-a-string rendered as the literal text
// "[object Object]" in every form on the app (KNOWN_INCONSISTENCIES #27); (3)
// Better Auth's own endpoints (change-password, sign-in, etc.) don't use this
// app's `{ error }` envelope at all — they return `{ message, code }` directly
// (confirmed live: wrong current password → `{"message":"Invalid
// password","code":"INVALID_PASSWORD"}`, no `error` key whatsoever). This
// unwraps all three down to one human-readable string, preferring the first
// Zod issue's own `.message` for case 2.
function extractErrorMessage(err) {
  if (typeof err?.error === 'string') return err.error;
  if (err?.error && typeof err.error === 'object') {
    try {
      const issues = JSON.parse(err.error.message);
      if (Array.isArray(issues) && issues[0]?.message) return issues[0].message;
    } catch {
      // err.error.message wasn't a JSON issue array — fall through
    }
    if (typeof err.error.message === 'string') return err.error.message;
  }
  if (typeof err?.message === 'string') return err.message; // Better Auth's own shape
  return 'Request failed';
}

// Thin fetch wrapper — all errors throw { status, message }
async function rawRequest(method, path, body) {
  const opts = {
    method,
    credentials: 'include',
    headers: {},
  };
  if (body !== undefined) {
    opts.headers['Content-Type'] = 'application/json';
    opts.body = JSON.stringify(body);
  }
  const res = await fetch(path, opts);
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw { status: res.status, message: extractErrorMessage(err) };
  }
  if (res.status === 204) return null;
  return res.json();
}

// ── GET response cache (KNOWN_INCONSISTENCIES U-4) ──────────────────────
// Every tab switch used to re-issue every GET the tab depends on (~14 calls
// across the app on a full render), even when nothing had changed since the
// last visit. app.js's switchTab() deliberately forces a fresh renderTab()
// call on *every* switch — that's a real fix for a real bug (a save used to
// leave the UI showing stale data until the next full switch, see its own
// comment) — but "always render fresh" doesn't require "always round-trip
// the network": caching short-circuits repeat GETs to the same URL within a
// small window, while any successful write clears the whole cache so the
// very next GET after a change is guaranteed to hit the network, preserving
// that fix's guarantee exactly.
//
// A full clear on write (not per-resource invalidation) is deliberate:
// several GETs are cross-entity aggregates (dashboard/pulse,
// dashboard/progress) that a write to almost any table can change, and
// guessing which URL prefixes are "safe" to leave cached is a real
// correctness risk (a stale aggregate silently surviving a write) for very
// little savings on an app this size. The CSV-import upload
// (progress.js) bypasses this file's request() with its own fetch() call —
// it clears the cache manually via api.clearCache() after a real (non-dry-run)
// import succeeds.
const GET_CACHE_TTL_MS = 30_000;
const getCache = new Map(); // path -> { expires: number, promise: Promise }

function clearCache() {
  getCache.clear();
}

function cachedGet(path) {
  const hit = getCache.get(path);
  if (hit && hit.expires > Date.now()) return hit.promise;
  const promise = rawRequest('GET', path);
  getCache.set(path, { expires: Date.now() + GET_CACHE_TTL_MS, promise });
  // Don't let a failed GET keep poisoning the cache for the rest of the TTL —
  // the next caller should get a fresh attempt, not a cached rejection.
  promise.catch(() => getCache.delete(path));
  return promise;
}

async function request(method, path, body) {
  if (method === 'GET') return cachedGet(path);
  const result = await rawRequest(method, path, body);
  clearCache(); // any successful write invalidates every cached read
  return result;
}

export const api = {
  clearCache,
  get:    (path)        => request('GET',    path),
  post:   (path, body)  => request('POST',   path, body),
  patch:  (path, body)  => request('PATCH',  path, body),
  delete: (path)        => request('DELETE', path),

  auth: {
    signIn:  (email, password) =>
      request('POST', '/api/auth/sign-in/email', { email, password }),
    signUp:  (name, email, password) =>
      request('POST', '/api/auth/sign-up/email', { name, email, password }),
    signOut: () =>
      request('POST', '/api/auth/sign-out', {}),
    session: () =>
      request('GET', '/api/auth/get-session'),
    // otp: required second factor, sent via api.settings.sendPasswordOtp()
    // and checked server-side (src/lib/auth.ts's hooks.before) before this
    // call is allowed to proceed — Better Auth's own change-password schema
    // doesn't know about the field, it's read directly off the raw body.
    changePassword: (currentPassword, newPassword, revokeOtherSessions = false, otp) =>
      request('POST', '/api/auth/change-password', { currentPassword, newPassword, revokeOtherSessions, otp }),
    requestPasswordReset: (email) =>
      request('POST', '/api/auth/request-password-reset', { email }),
    resetPassword: (newPassword, token) =>
      request('POST', '/api/auth/reset-password', { newPassword, token }),
    // Pre-signup email verification (src/routes/auth-otp.ts) — public, no
    // session yet. Send a 6-digit code to an unregistered email, then verify
    // it before the real signUp() call above is allowed to succeed.
    sendSignupOtp: (email) =>
      request('POST', '/api/signup-otp/send', { email }),
    verifySignupOtp: (email, otp) =>
      request('POST', '/api/signup-otp/verify', { email, otp }),
    // Change email (Settings) — Better Auth's email-otp plugin, requires an
    // active session. Sends a code to the NEW address; the current address
    // is never re-verified (changeEmail.verifyCurrentEmail: false).
    requestEmailChange: (newEmail) =>
      request('POST', '/api/auth/email-otp/request-email-change', { newEmail }),
    changeEmail: (newEmail, otp) =>
      request('POST', '/api/auth/email-otp/change-email', { newEmail, otp }),
  },

  tasks:    {
    list:   (params = {}) => {
      const qs = new URLSearchParams(params).toString();
      return request('GET', `/api/tasks${qs ? '?' + qs : ''}`);
    },
    create: (body)  => request('POST',  '/api/tasks',    body),
    update: (id, b) => request('PATCH', `/api/tasks/${id}`, b),
    delete: (id)    => request('DELETE', `/api/tasks/${id}`),
  },

  blocks: {
    list:        (params = {}) => {
      const qs = new URLSearchParams(params).toString();
      return request('GET', `/api/blocks${qs ? '?' + qs : ''}`);
    },
    schoolBlocks:(params = {}) => {
      const qs = new URLSearchParams(params).toString();
      return request('GET', `/api/blocks/school${qs ? '?' + qs : ''}`);
    },
    recurringBlocks: (params = {}) => {
      const qs = new URLSearchParams(params).toString();
      return request('GET', `/api/blocks/recurring${qs ? '?' + qs : ''}`);
    },
    create:      (body)  => request('POST',  '/api/blocks',    body),
    update:      (id, b) => request('PATCH', `/api/blocks/${id}`, b),
    delete:      (id)    => request('DELETE', `/api/blocks/${id}`),
    classes: {
      list:   ()        => request('GET',    '/api/blocks/classes'),
      create: (body)    => request('POST',   '/api/blocks/classes',     body),
      update: (id, b)   => request('PATCH',  `/api/blocks/classes/${id}`, b),
      delete: (id)      => request('DELETE', `/api/blocks/classes/${id}`),
    },
    rules: {
      list:   ()      => request('GET',    '/api/blocks/rules'),
      create: (body)  => request('POST',   '/api/blocks/rules',     body),
      update: (id, b) => request('PATCH',  `/api/blocks/rules/${id}`, b),
      delete: (id)    => request('DELETE', `/api/blocks/rules/${id}`),
      overrideInstance: (ruleId, body) => request('POST', `/api/blocks/rules/${ruleId}/override`, body),
      cancelInstance:   (ruleId, date) => request('DELETE', `/api/blocks/rules/${ruleId}/override/${date}`),
    },
  },

  income:   {
    list:        (params = {}) => {
      const qs = new URLSearchParams(params).toString();
      return request('GET', `/api/income${qs ? '?' + qs : ''}`);
    },
    create:      (body)  => request('POST', '/api/income', body),
    update:      (id, b) => request('PATCH', `/api/income/${id}`, b),
    delete:      (id)    => request('DELETE', `/api/income/${id}`),
    commission:  {
      list:   () => request('GET', '/api/income/commission'),
      create: (b) => request('POST', '/api/income/commission', b),
    },
  },

  expenses: {
    list:   (params = {}) => {
      const qs = new URLSearchParams(params).toString();
      return request('GET', `/api/expenses${qs ? '?' + qs : ''}`);
    },
    create: (body)  => request('POST',  '/api/expenses',    body),
    update: (id, b) => request('PATCH', `/api/expenses/${id}`, b),
    delete: (id)    => request('DELETE', `/api/expenses/${id}`),
  },

  trades: {
    list:   (params = {}) => {
      const qs = new URLSearchParams(params).toString();
      return request('GET', `/api/trades${qs ? '?' + qs : ''}`);
    },
    create: (body) => request('POST',  '/api/trades', body),
    update: (id, b)=> request('PATCH', `/api/trades/${id}`, b),
    delete: (id)   => request('DELETE', `/api/trades/${id}`),
  },

  networth: {
    list:   ()         => request('GET',    '/api/networth'),
    latest: ()         => request('GET',    '/api/networth/latest'),
    create: (body)     => request('POST',   '/api/networth', body),
    update: (id, body) => request('PATCH',  `/api/networth/${id}`, body),
    delete: (id)       => request('DELETE', `/api/networth/${id}`),
  },

  roth: {
    list:   ()         => request('GET',    '/api/roth'),
    create: (body)     => request('POST',   '/api/roth', body),
    update: (id, body) => request('PATCH',  `/api/roth/${id}`, body),
    delete: (id)       => request('DELETE', `/api/roth/${id}`),
  },

  weeklyReviews: {
    list:      ()          => request('GET',   '/api/weekly-reviews'),
    save:      (body)      => request('POST',  '/api/weekly-reviews', body),
    markPlanned: (weekStart) => request('PATCH', `/api/weekly-reviews/${weekStart}/plan`, {}),
  },

  settings: {
    get:           ()     => request('GET',   '/api/settings'),
    update:        (body) => request('PATCH', '/api/settings', body),
    updateProfile: (body) => request('PATCH', '/api/settings/profile', body),
    rateHistory:   ()     => request('GET',   '/api/settings/rate-history'),
    addRateHistory:(body) => request('POST',  '/api/settings/rate-history', body),
    deleteRateHistory:(id)=> request('DELETE',`/api/settings/rate-history/${id}`),
    getTimeAlloc:  ()     => request('GET',   '/api/settings/time-alloc'),
    updateTimeAlloc:(body) => request('POST',  '/api/settings/time-alloc', body),
    import:        (body) => request('POST',   '/api/settings/import', body),
    deleteAccount: (password) => request('DELETE', '/api/settings/account', { password }),
    sendPasswordOtp: () => request('POST', '/api/settings/send-password-otp', {}),
  },

  // Money taxonomy, deliberately separate from `categories` above (#63 / B-4).
  expenseCategories: Object.assign(
    () => request('GET', '/api/expense-categories'),
    {
      create: (body)     => request('POST',   '/api/expense-categories',     body),
      update: (id, body) => request('PATCH',  `/api/expense-categories/${id}`, body),
      delete: (id)       => request('DELETE', `/api/expense-categories/${id}`),
    },
  ),

  categories: Object.assign(
    (params = {}) => request('GET', '/api/categories'),
    {
      create: (body)    => request('POST',   '/api/categories',     body),
      update: (id, body) => request('PATCH',  `/api/categories/${id}`, body),
      delete: (id)       => request('DELETE', `/api/categories/${id}`),
    }
  ),

  onboarding: {
    status:      ()     => request('GET',    '/api/onboarding/status'),
    loadSample:  ()     => request('POST',   '/api/onboarding/sample-data'),
    clearSample: ()     => request('DELETE', '/api/onboarding/sample-data'),
  },

  dashboard: {
    pulse:    () => request('GET', '/api/dashboard/pulse'),
    progress: () => request('GET', '/api/dashboard/progress'),
    paydays:  (year) => request('GET', `/api/dashboard/paydays?year=${year}`),
    payPeriod:(date) => request('GET', `/api/dashboard/pay-period?date=${date}`),
  },
};
